import { routes } from "@/consts";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import { isEmpty } from "lodash";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import { Link } from "react-router-dom";
import {
  FileDownIcon,
  MapPin,
  Tag,
  Download,
  FileText,
  Package,
  MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ListofQuatations from "./list-of-Quatations";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { QuotationType } from "@/pages/buyer/home/components/lobby/responsiveSeller/Wedgits/QuotationDetailsModal";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/global-components/card";
import { Button } from "@/app/global-components/button";
import { Badge } from "@/app/global-components/badge";
import { motion } from "framer-motion";

interface Offer {
  id: string | null;
  isApplied: boolean;
  title: string;
  value: number;
  deliverDate: string;
  type: string;
  status: string;
  categories: string[];
  region: string;
  city: string;
  fileId: string | null;
  TenderNr: string;
  products: Array<{
    item: string;
    quantity: number;
    unit: string;
    attachment: { url: string } | null;
    notice: string;
  }>;
}

function TenderInfoSeller({
  offer,
  listOffers,
  totalPages,
  id,
}: {
  offer: Offer;
  listOffers: QuotationType[];
  totalPages: number;
  id: any;
}) {
  const { locale } = useLocale();
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const { t } = useTranslate(locale);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Planning":
        return "bg-blue-100 text-blue-800";
      case "Opened":
        return "bg-purple-100 text-purple-800";
      case "Closed":
        return "bg-orange-100 text-orange-800";
      case "Accepted":
        return "bg-green-100 text-green-800";
      case "Finished":
        return "bg-emerald-100 text-emerald-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-8">
      {/* Tender Information Card */}
      <Card className="border-0 bg-gradient-to-r from-gray-50 to-white">
        <CardHeader className="pb-4">
          <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            {t("tenders.details")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <Badge
              className={cn(
                "px-3 py-1 text-sm font-medium",
                getStatusColor(offer.status)
              )}
            >
              {offer.status}
            </Badge>
            <span className="text-sm text-gray-500">
              {t("tenders.TenderNr")}: {offer.TenderNr}
            </span>
          </div>

          {/* Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Categories */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl"
            >
              <Tag className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  {t("item.fields.categories")}
                </h4>
                <p className="text-sm text-gray-600">
                  {isEmpty(offer.categories)
                    ? t("general.Unavailabe")
                    : offer.categories.slice(0, 3).join(", ") +
                      (offer.categories.length > 3 ? "..." : "")}
                </p>
              </div>
            </motion.div>

            {/* Region */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-start gap-3 p-4 bg-purple-50 rounded-xl"
            >
              <MapPin className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  {t("company.fields.region")}
                </h4>
                <p className="text-sm text-gray-600">
                  {regionsData.find(
                    (region) => region.region_id === Number(offer.region)
                  )?.name_en || t("general.Unavailabe")}
                </p>
              </div>
            </motion.div>

            {/* City */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-start gap-3 p-4 bg-green-50 rounded-xl"
            >
              <MapPin className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  {t("company.fields.city")}
                </h4>
                <p className="text-sm text-gray-600">
                  {citiesData.find(
                    (city) => city.city_id === Number(offer.city)
                  )?.name_en || t("general.Unavailabe")}
                </p>
              </div>
            </motion.div>

            {/* Attachment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-start gap-3 p-4 bg-orange-50 rounded-xl"
            >
              <Download className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  {t("attachments.file")}
                </h4>
                {!offer.fileId ? (
                  <p className="text-sm text-gray-600">
                    {t("general.Unavailabe")}
                  </p>
                ) : (
                  <Link
                    to={offer.fileId}
                    target="_blank"
                    className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 transition-colors"
                  >
                    <FileDownIcon className="w-4 h-4" />
                    {t("actions.download")}
                  </Link>
                )}
              </div>
            </motion.div>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      {offer.products && offer.products.length > 0 && (
        <Card className="border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <Package className="w-5 h-5 text-emerald-600" />
              {t("offers.items")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                <div className="bg-gray-50 rounded-xl overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-5 bg-gray-100 p-4 font-semibold text-gray-900">
                    <div
                      className={cn(
                        locale === LANG.AR ? "text-right" : "text-left"
                      )}
                    >
                      {t("offers.item")}
                    </div>
                    <div
                      className={cn(
                        locale === LANG.AR ? "text-right" : "text-left"
                      )}
                    >
                      {t("checkout.fields.quantity")}
                    </div>
                    <div
                      className={cn(
                        locale === LANG.AR ? "text-right" : "text-left"
                      )}
                    >
                      {t("item.fields.unit")}
                    </div>
                    <div
                      className={cn(
                        locale === LANG.AR ? "text-right" : "text-left"
                      )}
                    >
                      {t("offers.attachment")}
                    </div>
                    <div
                      className={cn(
                        locale === LANG.AR ? "text-right" : "text-left"
                      )}
                    >
                      {t("orders.fields.notes")}
                    </div>
                  </div>

                  {/* Table Body */}
                  <div className="divide-y divide-gray-200">
                    {offer.products.map((product, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="grid grid-cols-5 p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="font-medium text-gray-900">
                          {product.item}
                        </div>
                        <div className="text-gray-600">{product.quantity}</div>
                        <div className="text-gray-600">{product.unit}</div>
                        <div>
                          {product.attachment ? (
                            <Link
                              to={product.attachment.url}
                              target="_blank"
                              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
                              rel="noopener noreferrer"
                            >
                              <FileDownIcon className="w-4 h-4" />
                              {t("actions.download")}
                            </Link>
                          ) : (
                            <span className="text-gray-400">
                              {t("general.Unavailabe")}
                            </span>
                          )}
                        </div>
                        <div className="text-gray-600 text-sm">
                          {product.notice || "-"}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {authUser?.role === ROLE.SELLER && (
        <Card className="border-0 bg-gradient-to-r from-emerald-50 to-blue-50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-emerald-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {t("quotations.submit")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t("quotations.information")}
                  </p>
                </div>
              </div>

              {!offer.isApplied && (
                <Link to={`${routes.quatation}/${id}`}>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200">
                    {t("quotations.submit")}
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quotations List */}
      {authUser?.role === ROLE.SELLER && (
        <Card className="border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              {t("quotations.list")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ListofQuatations
              listOffers={listOffers}
              totalPages={totalPages}
              offerID={id || ""}
              offerStatus={offer?.status || "Planning"}
            />
          </CardContent>
        </Card>
      )}

      {/* Login/Register Prompt */}
      {!authUser && (
        <Card className="border-0 bg-gradient-to-r from-blue-50 to-purple-50">
          <CardContent className="p-8 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t("quotations.information")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("quotations.information")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to={routes.login} state={{ role: ROLE.SELLER }}>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200">
                    {t("authentication.login")}
                  </Button>
                </Link>
                <Link to={routes.register}>
                  <Button
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-xl font-medium transition-all duration-200"
                  >
                    {t("workflow.createAccount.title")}
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default TenderInfoSeller;
